/**
 * TypeScript-style interfaces for Recording DTOs matching the Java backend
 * These interfaces ensure type safety and consistency with the backend API
 */

// Recording Thumbnail DTO
export const RecordingThumbnailDto = {
  id: null,
  url: null,
  timestamp: null,
  width: null,
  height: null
};

// Main Recording DTO matching Java RecordingDto
export const RecordingDto = {
  id: null,
  ownerId: null,
  title: null,
  description: null,
  duration: null, // in seconds
  size: null, // in bytes
  courtName: null,
  recordingDate: null, // LocalDate
  livestreamTitle: null,
  cameraName: null,
  
  // Storage info
  s3Key: null,
  presignedUrl: null,
  
  // Flags
  purchased: false,
  recordingUrl: null,
  
  // Thumbnails
  thumbnails: [], // Array of RecordingThumbnailDto
  
  price: null, // BigDecimal
  
  // Audit fields
  createdAt: null, // Instant
  lastUpdatedOn: null, // Instant
  expirationDate: null // LocalDate
};

// Purchase DTOs
export const LivestreamPurchaseDto = {
  id: null,
  purchase: null, // PurchaseDto
  livestream: null, // LivestreamDto
  livestreamPurchaseDate: null, // LocalDateTime
  price: null, // BigDecimal
  successUrl: null,
  cancelUrl: null,
  processId: null,
  discountCode: null
};

export const CheckoutSessionResponse = {
  id: null, // Stripe session ID
  url: null, // Stripe checkout URL
  amountInCents: null,
  currency: null,
  errorMessage: null
};

// Trim and Crop DTO - matches backend TrimAndCropDto
export const TrimAndCropDto = {
  startSeconds: null, // in seconds
  endSeconds: null, // in seconds
  cropDto: {
    x: null, // crop coordinates
    y: null,
    width: null,
    height: null,
    socialMediaPlatform: null // SocialMediaPlatform enum
  }
};

// Processing Info DTO - matches backend ProcessingInfoDto
export const ProcessingInfoDto = {
  videoProcessingStatus: null, // 'PENDING', 'PROCESSING', 'PROCESSED', 'FAILED'
  videoProcessingProgress: null, // 0-100
  videoProcessingErrorMessage: null,
  s3Key: null,
  preSignedUrl: null,
  videoId: null
};

// API Response wrapper
export const ApiEnvelope = {
  success: true,
  data: null,
  message: null,
  errors: []
};

// Pagination response
export const PageResponse = {
  content: [],
  pageable: {
    sort: {
      sorted: false,
      unsorted: true,
      empty: true
    },
    offset: 0,
    pageNumber: 0,
    pageSize: 20,
    paged: true,
    unpaged: false
  },
  last: false,
  totalElements: 0,
  totalPages: 0,
  size: 20,
  number: 0,
  sort: {
    sorted: false,
    unsorted: true,
    empty: true
  },
  first: true,
  numberOfElements: 0,
  empty: false
};

// Utility functions for working with DTOs
export const createRecordingDto = (data) => ({
  ...RecordingDto,
  ...data,
  thumbnails: data.thumbnails || []
});

export const createTrimAndCropDto = (data) => ({
  ...TrimAndCropDto,
  ...data
});

export const createCheckoutSessionResponse = (data) => ({
  ...CheckoutSessionResponse,
  ...data
});

export const createProcessingInfoDto = (data) => ({
  ...ProcessingInfoDto,
  ...data
});

// Validation helpers
export const isValidRecordingDto = (recording) => {
  return recording && 
         typeof recording.id === 'number' && 
         typeof recording.title === 'string' &&
         Array.isArray(recording.thumbnails);
};

export const isValidTrimAndCropDto = (dto) => {
  return dto && 
         typeof dto.startSeconds === 'number' && 
         typeof dto.endSeconds === 'number' &&
         dto.startSeconds >= 0 && 
         dto.endSeconds > dto.startSeconds &&
         dto.cropDto &&
         typeof dto.cropDto.x === 'number' &&
         typeof dto.cropDto.y === 'number' &&
         typeof dto.cropDto.width === 'number' &&
         typeof dto.cropDto.height === 'number';
};

// Status constants
export const PROCESSING_STATUS = {
  PENDING: 'PENDING',
  PROCESSING: 'PROCESSING', 
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED'
};

export const USER_ROLES = {
  ADMIN: 'ADMIN',
  SITE_OWNER: 'SITE_OWNER',
  USER: 'USER'
};

// Error types matching backend
export const ERROR_TYPES = {
  LIVESTREAM_NOT_FOUND: 'LIVESTREAM_NOT_FOUND',
  USER_NOT_FOUND: 'USER_NOT_FOUND',
  RECORDING_NOT_FOUND: 'RECORDING_NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  PROCESSING_ERROR: 'PROCESSING_ERROR'
};
